import { useEffect } from 'react'
import { Capacitor } from '@capacitor/core'
import { useLocation, useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { useI18n } from '../i18n'
import { Button, Card } from '../ui/kit'
import { oeffneExtern } from '../lib/extern'
import { oeffentlicheUrl } from '../lib/urls'
// Seit Vorgang 010 liegen die Anbieterangaben in `lib/anbieter.ts`: das Blatt an
// der Grenze der Selbstbedienung nennt dieselbe Adresse, und zwei Orte für eine
// Pflichtangabe laufen still auseinander.
import { ANBIETER } from '../lib/anbieter'
import { LEGAL_TERMS_2027 } from '../lib/legalTerms2027'
import { LEGAL_TERMS_ARCHIVE } from '../lib/legalTermsArchive'
import { LEGAL_DELETION_2026 } from '../lib/legalDeletion2026'
import { LEGAL_PRIVACY_2026, LEGAL_PRIVACY_RETENTION_2026 } from '../lib/legalPrivacy2026'

/**
 * Impressum und Datenschutzerklärung.
 *
 * WARUM IM PRODUKT UND NICHT AUF DER WEBSITE: § 5 ECG und Art. 13 DSGVO verlangen,
 * dass beides „leicht, unmittelbar und ständig zugänglich" ist — und zwar dort, wo
 * die Verarbeitung stattfindet. Wer in StaySuite angemeldet ist, sitzt nicht auf der
 * Website; ein Link, den man erst suchen muss, erfüllt die Pflicht nicht.
 *
 * WARUM VOR DER ANMELDESPERRE: beide Seiten müssen auch jemand aufrufen können, der
 * kein Konto hat oder sich gerade nicht anmelden kann — sonst hängt die
 * Pflichtinformation an einem Passwort.
 *
 * WAS HIER STEHT UND WAS NICHT: Der Text beschreibt AUVISO als Anbieter von
 * StaySuite. Er beschreibt NICHT die Verarbeitung, die der Vermieter selbst mit den
 * Daten seiner Gäste vornimmt — dafür ist der Vermieter der Verantwortliche und
 * braucht seine eigene Datenschutzerklärung. Der Abschnitt „Rollen" sagt das
 * ausdrücklich, weil diese Verwechslung der häufigste Fehler bei Software für kleine
 * Betriebe ist.
 */

/** Wann der Text zuletzt geändert wurde — gehört sichtbar unter jede Rechtsseite. */
const STAND = { de: '22. Oktober 2026', en: '22 October 2026' }

export default function LegalView() {
  const { lang, setLang } = useI18n()
  const navigate = useNavigate()
  const location = useLocation()
  const path = location.pathname
  const futureTerms = new URLSearchParams(location.search).get('fassung') === '2027-01-01'
  const englishLegacy = path === '/en/legal.html'
  const de = !englishLegacy && lang === 'de'
  useEffect(() => {
    if (englishLegacy && lang !== 'en') setLang('en')
  }, [englishLegacy, lang, setLang])
  const page: 'privacy' | 'terms' | 'imprint' | 'deletion' =
    path.startsWith('/datenschutz') || path.startsWith('/privacy') ? 'privacy'
    : path.startsWith('/agb') || path.startsWith('/terms') ? 'terms'
    : path.startsWith('/konto-loeschen') || path.startsWith('/delete-account') ? 'deletion'
    : 'imprint'

  const title = page === 'privacy' ? (de ? 'Datenschutzerklärung' : 'Privacy notice')
    : page === 'terms' ? (de ? 'Allgemeine Geschäftsbedingungen' : 'Terms and conditions')
    : page === 'deletion' ? (de ? 'Konto und Daten löschen' : 'Delete account and data')
    : (de ? 'Impressum' : 'Legal notice')

  // Querverweise auf die jeweils anderen beiden Seiten: wer eine Rechtsseite offen
  // hat, sucht meistens gleich die nächste.
  const others = ([
    ['imprint', '/impressum', de ? 'Impressum' : 'Legal notice'],
    ['privacy', '/datenschutz', de ? 'Datenschutzerklärung' : 'Privacy notice'],
    ['terms', '/agb', de ? 'AGB' : 'Terms'],
    ['deletion', '/konto-loeschen', de ? 'Konto löschen' : 'Delete account'],
  ] as const).filter(([key]) => key !== page)

  return (
    <div className="min-h-dvh bg-surface-2">
      <div className="max-w-2xl mx-auto p-4 pt-safe space-y-4">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft size={18} />
          </Button>
          <h1 className="text-xl font-semibold">{title}</h1>
        </div>

        <Card className="p-5 space-y-4 text-sm leading-relaxed">
          {page === 'privacy' ? <Privacy de={de} />
            : page === 'terms' ? <Terms de={de} future={futureTerms} />
            : page === 'deletion' ? <Deletion de={de} />
            : <Imprint de={de} />}
          <p className="text-xs text-muted pt-2 border-t border-border">
            {de ? 'Stand: ' : 'Last updated: '}{STAND[de ? 'de' : 'en']}
          </p>
        </Card>

        <p className="text-xs text-muted text-center pb-6 flex justify-center gap-4">
          {others.map(([key, href, label]) => (
            <a key={key} className="underline underline-offset-2" href={href}>{label}</a>
          ))}
        </p>
      </div>
    </div>
  )
}

function H({ children }: { children: React.ReactNode }) {
  return <h2 className="font-medium pt-2">{children}</h2>
}

/**
 * Konto und Daten löschen.
 *
 * WARUM ES DIESE SEITE GIBT: Google Play verlangt seit 2023 von jeder App, die
 * eine Kontoanlage anbietet, eine Löschseite, die **ohne die App und ohne
 * Anmeldung** erreichbar ist; ihre Adresse wird im Datensicherheitsformular
 * abgefragt. Fehlt sie, wird die App abgelehnt — es ist einer der häufigsten
 * Ablehnungsgründe überhaupt.
 *
 * Sie steht deshalb in derselben Reihe wie Impressum und Datenschutzerklärung,
 * vor der Anmeldesperre (`publicPath` in `App.tsx`). Der Grund ist auch ohne
 * Google einleuchtend: Wer sein Konto loswerden will, kommt womöglich gerade
 * nicht mehr hinein.
 *
 * DIE ZWEI ROLLEN, DIE HIER AUSEINANDERGEHALTEN WERDEN MÜSSEN — und der Grund,
 * warum diese Seite länger ist als ein Formular:
 *
 *   · Der **Vermieter** ist unser Kunde. Sein Konto löschen wir.
 *   · Der **Gast** ist Betroffener eines ANDEREN Verantwortlichen, nämlich
 *     seines Vermieters. StaySuite ist für Gastdaten nur Auftragsverarbeiter.
 *     Ein Gast, der hier landet, muss zu seinem Vermieter geschickt werden —
 *     wir dürfen seine Daten gar nicht auf Zuruf löschen, und täten wir es,
 *     wären es fremde Geschäftsunterlagen.
 *
 * Wer diese Unterscheidung wegkürzt, baut eine Seite, die entweder Gäste ins
 * Leere laufen lässt oder StaySuite zu einer Löschung verpflichtet, die es
 * rechtlich nicht vornehmen darf.
 *
 * SEIT VORGANG 019 steht der In-App-Weg ZUERST und der E-Mail-Weg als Rückfall
 * (AK10): Apple 5.1.1(v) verlangt den Anstoß in der App, Google verlangt diese
 * Seite — beide Wege nennen dieselbe Frist von 30 Tagen (Entscheid 1). Der
 * Wortlaut ist der F3-Vorschlag; Renés Abnahme (V4) steht aus, siehe
 * ADR-019-Nachtrag.
 */
function Deletion({ de }: { de: boolean }) {
  return de ? <DeletionDe /> : <DeletionEn />
}

function DeletionDe() {
  return (
    <>
      <p>
        Sie können Ihr StaySuite-Konto und die dazugehörigen Daten jederzeit löschen
        lassen. Diese Seite erklärt, wie das geht, was dabei gelöscht wird und was
        aus gesetzlichen Gründen bleiben muss.
      </p>

      <H>Sie sind Vermieter und haben ein Konto</H>
      <p>
        Der schnellste Weg führt durch die App: Öffnen Sie{' '}
        <strong>Einstellungen → Konto → „Konto löschen"</strong> und bestätigen Sie
        mit Ihrem Passwort. Die Löschung wird vorgemerkt und nach{' '}
        <strong>30 Tagen</strong> vollzogen. Melden Sie sich innerhalb dieser Frist
        wieder an, können Sie die Löschung mit einem Klick abbrechen.
      </p>
      <p>
        Alternativ schreiben Sie uns aus der E-Mail-Adresse, mit der Sie angemeldet
        sind, an{' '}
        <a className="underline underline-offset-2" href={`mailto:${ANBIETER.mail}?subject=Konto%20l%C3%B6schen`}>
          {ANBIETER.mail}
        </a>{' '}
        mit dem Betreff „Konto löschen". Mehr braucht es nicht — kein Formular, kein
        Kündigungsschreiben. Wir bestätigen den Eingang; auch auf diesem Weg gilt
        dieselbe Frist von <strong>30 Tagen</strong>.
      </p>
      <p>
        Die Frist ist keine Bearbeitungszeit, sondern Ihre Sicherheit: Sollte jemand
        anderes an Ihr Gerät oder Ihr Postfach gelangt sein, bleibt Ihnen Zeit, die
        Löschung zu stoppen. Melden Sie sich während der Frist an, zeigt die App
        vor allem anderen die Wahl, die Löschung abzubrechen oder dabei zu bleiben.
      </p>

      <H>Was gelöscht wird</H>
      <p>
        Ihr Zugang und Ihre Anmeldedaten, Ihre Mitgliedschaften und die
        personenbezogenen Daten Ihres Kontos. Eine spätere Registrierung mit
        derselben E-Mail-Adresse beginnt leer — Ihre bisherigen Daten sind dann
        nicht mehr zugänglich.
      </p>
      <p>{LEGAL_DELETION_2026.de.a}</p>

      <H>Was bleibt, und warum</H>
      {LEGAL_DELETION_2026.de.b.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}

      <H>Sie sind Gast und haben hier übernachtet</H>
      <p>
        Dann sind Sie bei uns an der falschen Adresse — und das ist kein Ausweichen,
        sondern der Rechtsweg. Über Ihre Daten entscheidet <strong>Ihr Vermieter</strong>;
        er ist der Verantwortliche im Sinne der DSGVO. StaySuite ist für ihn nur
        Auftragsverarbeiter und darf seine Geschäftsunterlagen nicht auf Zuruf
        Dritter ändern.
      </p>
      <p>
        Wenden Sie sich an den Betrieb, bei dem Sie gebucht haben — die Adresse
        steht auf Ihrer Rechnung oder Ihrer Buchungsbestätigung. Ihr Vermieter kann
        Ihre Daten unmittelbar in StaySuite löschen oder anonymisieren; die Funktion
        ist eingebaut, und sie zeigt ihm vorher an, was verschwindet und was aus den
        oben genannten Gründen bleiben muss.
      </p>
      <p>
        Kommen Sie dort nicht weiter, schreiben Sie uns an{' '}
        <a className="underline underline-offset-2" href={`mailto:${ANBIETER.mail}`}>
          {ANBIETER.mail}
        </a>. Wir können Ihre Daten zwar nicht selbst löschen, aber wir leiten Ihr
        Anliegen an den Betrieb weiter und weisen ihn auf seine Pflichten hin.
      </p>

      <H>Ohne Konto ausprobiert?</H>
      <p>
        Wenn Sie StaySuite nur als Demo angesehen haben, liegen keine Daten bei uns.
        Die Demo läuft ausschließlich in Ihrem Browser. Sie löschen sie, indem Sie
        die Websitedaten für diese Seite entfernen oder in der App die Demo
        zurücksetzen.
      </p>
    </>
  )
}

function DeletionEn() {
  return (
    <>
      <p>
        You can have your StaySuite account and its data deleted at any time. This
        page explains how, what gets deleted, and what has to be kept for legal
        reasons.
      </p>

      <H>You are a landlord with an account</H>
      <p>
        The quickest way is inside the app: open{' '}
        <strong>Settings → Account → “Delete account”</strong> and confirm with your
        password. The deletion is scheduled and carried out after{' '}
        <strong>30 days</strong>. If you sign in again within that period, you can
        cancel the deletion with one click.
      </p>
      <p>
        Alternatively, email us from the address you sign in with, to{' '}
        <a className="underline underline-offset-2" href={`mailto:${ANBIETER.mail}?subject=Delete%20account`}>
          {ANBIETER.mail}
        </a>, subject “Delete account”. Nothing else is needed — no form, no written
        notice. We confirm receipt; the same <strong>30-day</strong> period applies
        on this route too.
      </p>
      <p>
        That period is not processing time; it is your safety net. Should someone
        else have gained access to your device or mailbox, you still have time to
        stop the deletion. If you sign in during the period, the app shows you,
        before anything else, the choice to cancel the deletion or keep it.
      </p>

      <H>What is deleted</H>
      <p>
        Your sign-in and credentials, your memberships and the personal data of
        your account. A later registration with the same email address starts
        empty — your previous data is no longer accessible.
      </p>
      <p>{LEGAL_DELETION_2026.en.a}</p>

      <H>What remains, and why</H>
      {LEGAL_DELETION_2026.en.b.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}

      <H>You are a guest who stayed somewhere</H>
      <p>
        Then you have reached the wrong party — and that is the legal route, not a
        brush-off. Your data is controlled by <strong>your host</strong>; they are
        the controller under the GDPR. StaySuite acts only as their processor and
        may not alter their business records at a third party’s request.
      </p>
      <p>
        Contact the property you booked with — the address is on your invoice or
        booking confirmation. Your host can delete or anonymise your data directly
        in StaySuite; the function is built in, and it shows them beforehand what
        disappears and what must remain for the reasons above.
      </p>
      <p>
        If you get nowhere there, write to us at{' '}
        <a className="underline underline-offset-2" href={`mailto:${ANBIETER.mail}`}>
          {ANBIETER.mail}
        </a>. We cannot delete your data ourselves, but we will pass your request to
        the property and remind them of their obligations.
      </p>

      <H>Only tried the demo?</H>
      <p>
        If you only looked at StaySuite as a demo, we hold no data about you. The
        demo runs entirely in your browser. Clear the site data for this page, or
        reset the demo inside the app.
      </p>
    </>
  )
}

function Imprint({ de }: { de: boolean }) {
  return (
    <>
      <p>
        {de
          ? 'Angaben nach § 5 E-Commerce-Gesetz und § 25 Mediengesetz.'
          : 'Information pursuant to § 5 of the Austrian E-Commerce Act.'}
      </p>

      <H>{de ? 'Anbieter von StaySuite' : 'Provider of StaySuite'}</H>
      <address className="not-italic">
        {ANBIETER.name}<br />
        {ANBIETER.street}<br />
        {ANBIETER.city}<br />
        {ANBIETER.country}
      </address>

      <H>{de ? 'Kontakt' : 'Contact'}</H>
      <p>
        <a className="underline underline-offset-2" href={`mailto:${ANBIETER.mail}`}>
          {ANBIETER.mail}
        </a>
        <br />
        {/* `target="_blank"` wird in einer WebView blockiert — der Link tat dort
            nichts. Über `oeffneExtern` öffnet er sich über der App; im Browser
            bleibt es ein gewöhnlicher Sprung. `href` bleibt stehen, damit die
            Adresse sichtbar ist und sich kopieren lässt. */}
        <a className="underline underline-offset-2" href={`https://${ANBIETER.web}`}
          rel="noreferrer"
          onClick={(e) => { e.preventDefault(); void oeffneExtern(`https://${ANBIETER.web}`) }}>
          {ANBIETER.web}
        </a>
      </p>

      <H>{de ? 'Registerdaten' : 'Company register'}</H>
      <p>
        {de ? 'Firmenbuchnummer' : 'Company number'}: {ANBIETER.fn}<br />
        {de ? 'Umsatzsteuer-Identifikationsnummer' : 'VAT identification number'}: {ANBIETER.uid}
      </p>

      <H>{de ? 'Unternehmensgegenstand' : 'Business activity'}</H>
      <p>
        {de
          ? 'Entwicklung und Betrieb von Software; StaySuite ist eine Software zur Verwaltung von Vermietungen.'
          : 'Software development and operation; StaySuite is software for managing short-term rentals.'}
      </p>

      <H>{de ? 'Aufsichtsbehörde und Gewerbeordnung' : 'Supervisory authority'}</H>
      <p>
        {de
          ? 'Bezirkshauptmannschaft Innsbruck. Es gilt die Gewerbeordnung 1994 (www.ris.bka.gv.at).'
          : 'Bezirkshauptmannschaft Innsbruck, Austria. The Austrian Trade Act 1994 applies (www.ris.bka.gv.at).'}
      </p>

      <H>{de ? 'Streitbeilegung' : 'Dispute resolution'}</H>
      <p>
        {de
          ? 'Wir sind nicht verpflichtet und nicht bereit, an einem Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.'
          : 'We are neither obliged nor willing to take part in dispute resolution proceedings before a consumer arbitration board.'}
      </p>

      <H>{de ? 'Verantwortlich für den Inhalt' : 'Responsible for content'}</H>
      <p>{ANBIETER.name}, {ANBIETER.city}.</p>
    </>
  )
}

function PrivacySection({ lang, section }: { lang: 'de' | 'en'; section: '3' | '5' | '5b' | '7' | '9' }) {
  const text = section === '7' || section === '9'
    ? LEGAL_PRIVACY_RETENTION_2026[lang][section]
    : LEGAL_PRIVACY_2026[lang][section]
  return <section data-privacy-section={section}>
    <H>{text.title}</H>
    {section !== '3' && section !== '5'
      ? text.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)
      : <><ul className="list-disc pl-5 space-y-1">
        {text.paragraphs.slice(0, section === '5' ? -1 : undefined).map((paragraph) => <li key={paragraph}>{paragraph}</li>)}
      </ul>{section === '5' && <p>{text.paragraphs[text.paragraphs.length - 1]}</p>}</>}
  </section>
}

function Privacy({ de }: { de: boolean }) {
  if (!de) return <PrivacyEn />
  return (
    <>
      <H>1. Verantwortlicher</H>
      <p>
        {ANBIETER.name}, {ANBIETER.street}, {ANBIETER.city}, {ANBIETER.country}.
        Anfragen zum Datenschutz an{' '}
        <a className="underline underline-offset-2" href={`mailto:${ANBIETER.mail}`}>
          {ANBIETER.mail}
        </a>.
      </p>

      <H>2. Zwei getrennte Rollen</H>
      <p>
        Diese Erklärung beschreibt, wie AUVISO die Daten der <strong>Nutzer von
        StaySuite</strong> verarbeitet — also der Vermieterinnen und Vermieter, die
        ein Konto haben.
      </p>
      <p>
        Für die Daten der <strong>Gäste</strong>, die ein Vermieter in StaySuite
        erfasst, ist der jeweilige Vermieter der Verantwortliche; AUVISO verarbeitet
        sie ausschließlich in dessen Auftrag auf Grundlage eines
        Auftragsverarbeitungsvertrags nach Art. 28 DSGVO. Wer als Gast wissen will,
        was mit seinen Daten geschieht, wendet sich an seinen Gastgeber.
      </p>

      <PrivacySection lang="de" section="3" />

      <H>4. Keine Zählpixel, keine Werbung</H>
      <p>
        StaySuite setzt keine Cookies zu Analyse- oder Werbezwecken und bindet keine
        Dienste Dritter zum Nachverfolgen des Verhaltens ein. Gespeichert wird im
        Browser nur, was die Anwendung zum Funktionieren braucht — die Anmeldesitzung
        und ein paar Einstellungen wie Sprache und Zeilenhöhe im Kalender.
      </p>

      <PrivacySection lang="de" section="5" />

      <H>5a. Wenn Sie eine KI-Funktion aufrufen</H>
      <p>
        Die Preisberatung wird von Ihnen aufgerufen. Übermittelt werden begrenzte
        Auswahlwerte und Zahlen, etwa zu Land, Saison, Einheitenart, Größe,
        Kapazität, Datum, Währung, Preisen und Belegungsquoten. Freie Anfragetexte,
        Namen, Ausstattungstexte und frei formulierte Zusatzangaben sind kein
        Bestandteil dieser Aufträge. Das Auslesen eingefügter Anfragetexte ist
        derzeit pausiert; Anfragen können weiterhin manuell bearbeitet werden.
        Gesundheitsdaten und sonstige besonders geschützte personenbezogene Angaben
        gehören nicht zum zugelassenen KI-Auftrag.
      </p>
      <p>
        Getrennt vom Auftrag wird je Betrieb ein Nutzungsnachweis geführt. Er
        enthält Zeitpunkt, Modell, Eingabe- und Ausgabe-Tokenmengen,
        Kosten- und Aufschlagswerte sowie Benutzerkennung und E-Mail-Adresse des
        auslösenden Nutzers. Der Nutzungsnachweis enthält keine Auftragstexte.
        Daraus folgt keine Aussage über die Speicher- oder Löschpraxis des
        KI-Anbieters.
      </p>

      <PrivacySection lang="de" section="5b" />

      <H>6. Verbindet der Vermieter eigene Dienste</H>
      <p>
        Verbindet ein Vermieter sein eigenes Stripe-Konto, ein Buchungsportal per
        iCal-Kalender oder eine Registrierkasse, so bestehen diese Verbindungen
        zwischen ihm und dem jeweiligen Anbieter. StaySuite übermittelt nur, was für
        die Funktion nötig ist, und speichert die Zugangsdaten verschlüsselt.
      </p>

      <PrivacySection lang="de" section="7" />

      <H>8. Ihre Rechte</H>
      <p>
        Sie haben das Recht auf Auskunft (Art. 15), Berichtigung (Art. 16), Löschung
        (Art. 17), Einschränkung der Verarbeitung (Art. 18), Datenübertragbarkeit
        (Art. 20) und Widerspruch (Art. 21 DSGVO). Wenden Sie sich dafür an die oben
        genannte Adresse.
      </p>
      <p>
        Sie können sich außerdem bei der Aufsichtsbehörde beschweren:
        Österreichische Datenschutzbehörde, Barichgasse 40–42, 1030 Wien,
        dsb.gv.at.
      </p>

      <PrivacySection lang="de" section="9" />
    </>
  )
}

function PrivacyEn() {
  return (
    <>
      <H>1. Controller</H>
      <p>
        {ANBIETER.name}, {ANBIETER.street}, {ANBIETER.city}, Austria. Data protection
        enquiries to{' '}
        <a className="underline underline-offset-2" href={`mailto:${ANBIETER.mail}`}>
          {ANBIETER.mail}
        </a>.
      </p>

      <H>2. Two separate roles</H>
      <p>
        This notice describes how AUVISO processes the data of <strong>StaySuite
        users</strong> — the landlords who hold an account.
      </p>
      <p>
        For the data of <strong>guests</strong> that a landlord records in StaySuite,
        that landlord is the controller; AUVISO processes it solely on their
        instructions under a data processing agreement pursuant to Art. 28 GDPR. A
        guest with questions about their data should contact their host.
      </p>

      <PrivacySection lang="en" section="3" />

      <H>4. No tracking, no advertising</H>
      <p>
        StaySuite sets no analytics or advertising cookies and embeds no third-party
        tracking. The browser stores only what the application needs to work — the
        session and a few preferences such as language and calendar row height.
      </p>

      <PrivacySection lang="en" section="5" />

      <H>5a. When you invoke an AI feature</H>
      <p>
        You initiate price advice. Transmitted data comprises bounded selections
        and numbers, for example country, season, unit type, size, capacity,
        date, currency, prices and occupancy percentages. Free enquiry texts,
        names, amenity descriptions and free-form additional information are not
        part of these requests. Reading pasted enquiries is currently paused;
        enquiries can still be handled manually. Health data and other specially
        protected personal information are outside the permitted AI request scope.
      </p>
      <p>
        A separate usage record is kept per business. It contains the time, model,
        input and output token quantities, cost and markup values, and the user
        identifier and email address of the initiating user. The usage record
        contains no request texts. This does not establish the AI provider's
        storage or deletion practices.
      </p>

      <PrivacySection lang="en" section="5b" />

      <H>6. Services the landlord connects</H>
      <p>
        Where a landlord connects their own Stripe account, a booking portal by iCal
        feed or a fiscal register, those connections exist between them and the
        provider concerned. StaySuite transmits only what the function requires and
        stores the credentials encrypted.
      </p>

      <PrivacySection lang="en" section="7" />

      <H>8. Your rights</H>
      <p>
        You have the right of access (Art. 15), rectification (Art. 16), erasure
        (Art. 17), restriction (Art. 18), portability (Art. 20) and objection
        (Art. 21 GDPR). You may also lodge a complaint with the Austrian Data
        Protection Authority, Barichgasse 40–42, 1030 Vienna, dsb.gv.at.
      </p>

      <PrivacySection lang="en" section="9" />
    </>
  )
}

/**
 * Die AGB zwischen AUVISO und dem Vermieter — nicht die zwischen dem Vermieter und
 * seinem Gast. Die zweiten schreibt jeder Betrieb selbst; sie stehen als
 * Stornobedingungen an seinen Angeboten.
 *
 * WAS HIER BEWUSST NICHT STEHT: Preise als feste Vertragsanlage. Maßgeblich ist,
 * was im Bestellvorgang angezeigt wird — sonst müsste jede Preisänderung eine
 * Vertragsänderung sein. Die derzeit geltenden Beträge stehen trotzdem im Text,
 * weil ein Kunde sie lesen können soll, ohne den Bestellvorgang zu starten.
 *
 * WAS DER ABSCHNITT „PFLICHTEN DES KUNDEN" LEISTEN MUSS: klarstellen, dass StaySuite
 * Werkzeuge liefert und keine Rechtsberatung. Registrierkasse, Meldewesen, Ortstaxe
 * und die Richtigkeit von Rechnungen bleiben Sache des Betriebs. Ohne diesen Satz
 * entsteht der Eindruck, die Software garantiere Gesetzeskonformität — und genau
 * dieser Eindruck ist die teuerste Zusage, die man versehentlich machen kann.
 */
/** Die Fassung wird ausdrücklich gewählt, niemals anhand der Geräteuhr aktiviert. */
function Terms({ de, future }: { de: boolean; future: boolean }) {
  const path = useLocation().pathname
  const archive = LEGAL_TERMS_ARCHIVE[de ? 'de' : 'en']
  const native = Capacitor.isNativePlatform()
  const notice = LEGAL_TERMS_2027[de ? 'de' : 'en'].notice
  return <>
    <section aria-label={de ? 'Vertragsfassung' : 'Terms version'} className="space-y-2 rounded-xl border border-border p-3">
      <strong>{future
        ? (de ? 'Neufassung für Verträge ab 1. Januar 2027' : 'New version for contracts from 1 January 2027')
        : (de ? 'Derzeitige Vertragsfassung: 18. August 2026' : 'Current terms version: 18 August 2026')}</strong>
      {!future && <p>{de
        ? 'Diese vollständige bisherige Fassung bleibt vor neuen Bestellungen erreichbar. Die Veröffentlichung der Neufassung ändert bestehende Verträge nicht.'
        : 'The existing version remains available before new orders. Publication of the new version does not amend existing contracts.'}</p>}
      <div className="flex flex-wrap gap-3">
        <a className="underline" href={`${path}?fassung=2026-08-18`}>{de ? 'Bisherige Fassung lesen' : 'Read existing version'}</a>
        <a className="underline" href={`${path}?fassung=2027-01-01`}>{de ? 'Neufassung ab 1. Januar 2027 lesen' : 'Read new version from 1 January 2027'}</a>
        <a className="underline" href={archive.path} download={!native} onClick={(event) => {
          if (!native) return
          event.preventDefault()
          void oeffneExtern(oeffentlicheUrl(archive.path))
        }}>{native
          ? (de ? 'Bisherige Fassung öffnen' : 'Open existing version')
          : (de ? 'Bisherige Fassung als Text sichern' : 'Save existing version as text')}</a>
      </div>
      <p className="break-all text-xs text-muted">SHA-256: {archive.sha256}</p>
    </section>
    {future && <section aria-label={notice.title} className="space-y-4">
      <H>{notice.title}</H>
      {notice.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
      {!de && <p>{LEGAL_TERMS_2027.en.intro}</p>}
    </section>}
    <div data-terms-body className="space-y-4">{de ? <TermsDe future={future} /> : <TermsEn future={future} />}</div>
  </>
}

function FutureTermsSection({ de, section }: { de: boolean; section: '9' | '11' | '12' }) {
  const text = LEGAL_TERMS_2027[de ? 'de' : 'en'].sections[section]
  return <section data-terms-section={section} className="space-y-4">
    <H>{text.title}</H>
    {text.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
  </section>
}

function TermsDe({ future }: { future: boolean }) {
  return (
    <>
      <p className="text-muted">
        Diese Bedingungen gelten für die Überlassung der Software StaySuite durch
        die {ANBIETER.name} an Unternehmerinnen und Unternehmer, die Unterkünfte
        vermieten. Verbraucher im Sinne des KSchG sind nicht Zielgruppe dieses
        Angebots.
      </p>

      <H>1. Vertragspartner und Gegenstand</H>
      <p>
        Vertragspartner ist die {ANBIETER.name}, {ANBIETER.street}, {ANBIETER.city}{' '}
        ({ANBIETER.fn}, UID {ANBIETER.uid}). Gegenstand ist die Bereitstellung der
        Software StaySuite zur Nutzung über das Internet (Software as a Service)
        samt Speicherplatz für die dabei entstehenden Daten. Eine Übergabe von
        Programmkopien findet nicht statt.
      </p>

      <H>2. Leistungsumfang</H>
      <p>
        StaySuite umfasst Belegungskalender, Preis- und Angebotsverwaltung,
        Rechnungslegung, Gästekartei, Online-Check-in, Portalkalender (iCal),
        Zahlungsanbindung sowie Auswertungen. Der Leistungsumfang ist in allen
        Paketen derselbe; die Pakete unterscheiden sich allein in der Zahl der
        Einheiten. Die Selbstbedienung reicht bis fünfzehn Einheiten, darüber
        gilt ein individuelles Angebot. Der jeweils gebuchte Umfang ergibt sich
        aus dem Bestellvorgang.
      </p>
      <p>
        Die Software wird laufend weiterentwickelt. Funktionen können hinzukommen,
        sich ändern oder entfallen, solange der vertraglich geschuldete Kern erhalten
        bleibt. Wesentliche Einschränkungen werden vorher angekündigt.
      </p>

      <H>3. Testphase</H>
      <p>
        Die Testphase beträgt 30 Tage ab Anlage des Kontos. Sie endet automatisch
        und geht nicht von selbst in ein kostenpflichtiges Abonnement über.{' '}
        {future
          ? 'Wer das Abonnement nicht beauftragt, verliert nach Ablauf der Testphase den regulären Zugriff. Für die Rückgabe und Löschung der Daten gelten Ziffer 9 und die zwingenden gesetzlichen Rechte.'
          : <>Wer das Abonnement nicht beauftragt, verliert nach Ablauf den Zugriff; die Daten bleiben für den in Punkt 9 genannten Zeitraum abrufbar.</>}
      </p>

      <H>4. Entgelte und Zahlung</H>
      <p>
        Maßgeblich sind die im Bestellvorgang angezeigten Preise. Das Entgelt
        richtet sich nach dem gebuchten Paket, also nach der Zahl der Einheiten,
        und ist im Jahresvertrag je Monat niedriger als im Monatsabo. Alle
        Preise verstehen sich zuzüglich Umsatzsteuer. Abgerechnet wird im
        Voraus über den Zahlungsdienstleister Stripe per SEPA-Lastschrift oder
        Karte; dafür ist ein gültiges Mandat zu hinterlegen.
      </p>
      <p>
        Bleibt eine Zahlung aus, wird der Zugang nach einer Mahnung und einer
        Nachfrist von 14 Tagen eingeschränkt. Die Daten werden dadurch nicht
        gelöscht.
      </p>

      <H>5. Laufzeit und Kündigung</H>
      <p>
        Das monatliche Abonnement hat eine Mindestlaufzeit von einem Monat und
        verlängert sich um jeweils einen Monat, wenn es nicht mit einer Frist von
        14 Tagen zum Laufzeitende gekündigt wird. Der Jahresvertrag läuft zwölf
        Monate und verlängert sich um jeweils zwölf Monate, wenn er nicht mit einer
        Frist von einem Monat zum Laufzeitende gekündigt wird. Die Kündigung ist in
        Textform möglich, eine E-Mail genügt. Das Recht zur Auflösung aus wichtigem
        Grund bleibt beiden Seiten unberührt.
      </p>

      <H>6. Preisanpassung</H>
      <p>
        Die Entgelte können höchstens einmal je Vertragsjahr angepasst werden. Eine
        Anpassung wird mindestens acht Wochen vorher in Textform angekündigt.
        Erhöht sich das Entgelt, kann der Vertrag bis zum Wirksamwerden der
        Anpassung zum Zeitpunkt ihres Inkrafttretens gekündigt werden; darauf wird
        in der Ankündigung hingewiesen.
      </p>

      <H>7. Verfügbarkeit</H>
      <p>
        AUVISO bemüht sich um eine hohe Verfügbarkeit, schuldet aber keine bestimmte
        Verfügbarkeitsquote, solange kein Service Level Agreement vereinbart ist.
        Wartungsarbeiten werden nach Möglichkeit außerhalb der üblichen
        Geschäftszeiten durchgeführt. Ausfälle bei eingesetzten Dienstleistern
        (siehe Datenschutzerklärung) sind AUVISO nicht zuzurechnen, soweit sie nicht
        verschuldet sind.
      </p>

      <H>8. Pflichten des Kunden</H>
      <p>
        Der Kunde hält seine Stammdaten aktuell — insbesondere Anschrift, UID und
        Bankverbindung, weil sie auf jeder Rechnung erscheinen. Zugangsdaten sind
        geheim zu halten.
      </p>
      <p>
        <strong>StaySuite stellt Werkzeuge bereit, keine Rechtsberatung.</strong> Die
        Einhaltung der für den Betrieb geltenden Vorschriften bleibt Sache des
        Kunden. Das betrifft insbesondere die Registrierkassen- und Belegerteilungs­pflicht,
        das Meldewesen, die Ortstaxe, die Richtigkeit der ausgestellten Rechnungen
        sowie die eigenen Informationspflichten gegenüber Gästen. AUVISO schuldet
        weder die steuerliche noch die rechtliche Richtigkeit der vom Kunden
        erzeugten Dokumente.
      </p>

      {future ? <FutureTermsSection de section="9" /> : (<>
      <H>9. Daten</H>
      <p>
        Die vom Kunden eingegebenen Daten bleiben seine Daten. Für die Daten seiner
        Gäste ist der Kunde Verantwortlicher im Sinne der DSGVO; AUVISO verarbeitet
        sie als Auftragsverarbeiter auf Grundlage eines Vertrags nach Art. 28 DSGVO.
        Einzelheiten und die eingesetzten Dienstleister nennt die
        Datenschutzerklärung. Die Datenbank wird in einem Rechenzentrum innerhalb
        der EU gehalten.
      </p>
      <p>
        Nach Vertragsende bleiben die Daten 30 Tage abrufbar; ein Export ist
        jederzeit möglich. Danach werden sie gelöscht, soweit keine gesetzliche
        Aufbewahrungspflicht entgegensteht.
      </p>

      </>)}

      <H>10. Rechte an der Software</H>
      <p>
        Der Kunde erhält für die Vertragslaufzeit ein nicht ausschließliches, nicht
        übertragbares Recht zur Nutzung im gebuchten Umfang. Eine Weitergabe an
        Dritte, ein Nachbau oder ein Zugriff mit automatisierten Mitteln außerhalb
        der vorgesehenen Schnittstellen ist nicht gestattet.
      </p>

      {future ? <FutureTermsSection de section="11" /> : (<>
      <H>11. Gewährleistung und Haftung</H>
      <p>
        AUVISO haftet uneingeschränkt für Vorsatz und grobe Fahrlässigkeit sowie bei
        Verletzung von Leben, Körper oder Gesundheit. Bei leichter Fahrlässigkeit
        wird nur für die Verletzung wesentlicher Vertragspflichten und der Höhe nach
        begrenzt auf den typischerweise vorhersehbaren Schaden gehaftet, höchstens
        auf das im betroffenen Vertragsjahr gezahlte Entgelt. Für entgangenen Gewinn
        und mittelbare Schäden wird bei leichter Fahrlässigkeit nicht gehaftet.
        Zwingende Ansprüche nach dem Produkthaftungsgesetz bleiben unberührt.
      </p>
      <p>
        Der Kunde sichert Daten, die er außerhalb von StaySuite benötigt, selbst.
        AUVISO führt Sicherungen im Rahmen des Betriebs durch; sie ersetzen keine
        eigene Datensicherung des Kunden.
      </p>

      </>)}

      {future ? <FutureTermsSection de section="12" /> : (<>
      <H>12. Änderungen dieser Bedingungen</H>
      <p>
        Änderungen werden mindestens sechs Wochen vor ihrem Wirksamwerden in
        Textform angekündigt. Widerspricht der Kunde nicht bis zum Wirksamwerden,
        gelten sie als angenommen; darauf wird in der Ankündigung hingewiesen. Im
        Fall des Widerspruchs kann jede Seite den Vertrag zum Wirksamwerden kündigen.
      </p>

      </>)}

      <H>13. Recht und Gerichtsstand</H>
      <p>
        Es gilt österreichisches Recht unter Ausschluss des UN-Kaufrechts.
        Erfüllungsort ist Innsbruck. Für Streitigkeiten aus diesem Vertrag wird die
        Zuständigkeit des sachlich zuständigen Gerichts in Innsbruck vereinbart,
        soweit der Kunde Unternehmer ist. Für Kunden mit Sitz in Deutschland gilt
        zwingendes deutsches Recht, soweit es nicht abbedungen werden kann.
      </p>
      <p>
        Änderungen dieses Vertrags bedürfen der Textform. Sollte eine Bestimmung
        unwirksam sein, bleibt der übrige Vertrag wirksam.
      </p>
    </>
  )
}

function TermsEn({ future }: { future: boolean }) {
  return (
    <>
      <p className="text-muted">
        These terms govern the provision of the StaySuite software by {ANBIETER.name}
        {' '}to businesses letting accommodation. This offer is not aimed at consumers.
      </p>
      <H>Summary</H>
      <p>
        StaySuite is provided as software as a service: a 30-day trial that does not
        roll over by itself, then the price of the plan you booked — plans differ by
        the number of units, not by features — plus VAT, collected through Stripe.
        Self-service goes up to fifteen units; above that there is an individual
        offer. Either side may terminate in text form — 14 days for the monthly
        plan, one month for the annual one. Prices may be adjusted at most once per contract year with eight
        weeks' notice and a right of termination.
      </p>
      {future ? <FutureTermsSection de={false} section="9" /> : (<>
      <p>
        Your data stays yours and is held in an EU data centre; for your guests' data
        you are the controller and AUVISO is a processor under Art. 28 GDPR. After the
        contract ends the data remains exportable for 30 days.
      </p>
      </>)}
      <p>
        <strong>StaySuite provides tools, not legal advice.</strong> Compliance with
        the rules that apply to your business — fiscal registers, guest registration,
        local visitor taxes and the correctness of the invoices you issue — remains
        yours.
      </p>
      {future ? (<>
        <p>Austrian law applies, place of performance is Innsbruck.</p>
        <FutureTermsSection de={false} section="11" />
        <FutureTermsSection de={false} section="12" />
      </>) : (<>
      <p>
        Austrian law applies, place of performance is Innsbruck. Liability is
        unlimited for intent and gross negligence and for injury to life, body or
        health; for slight negligence it is limited to essential contractual duties
        and to the fee paid in the contract year concerned.
      </p>
      </>)}
      <p className="text-muted">
        {future
          ? 'The German version is binding; this English summary and the translated replacement sections are provided for information.'
          : <>The German version above is the operative text; this summary is provided for convenience.</>}
      </p>
    </>
  )
}
